import { useMemo } from 'react';
import { isTruthy } from '@core/helpers/boolean';
import { isVideoHorizontal } from '@core/helpers/video/isVideoHorizontal';
import { isLiveStream, isLiveStreamLiveOrReplay, } from '@core/livestream/helpers';
import { useLiveStreamActiveUser } from '@core/livestream/hooks/useLiveStreamActiveUser';
import { PlayerLayout } from '@core/player/types';
import { areStoryblockParams } from '@core/types';
import { AppContextType } from '@embed/components/helpers';
import { useLayoutOrientation } from '@embed/components/VideoPlayer/Player3/hooks/useLayoutOrientation';
import { useStore } from '@embed/components/VideoPlayer/Player3/store';
import { useVideoPlayerStore } from '@embed/components/VideoPlayer/store';
import { videoPlayerAvailableHeightSelector, videoPlayerAvailableWidthSelector, videoPlayerIsMinimizedSelector, videoPlayerLayoutSelector, } from '@embed/components/VideoPlayer/store/selectors';
import { useProducts } from '@player/features/product/hooks/useProducts';
import { forceSmallPortraitLayout } from '@player/utils/forceSmallPortraitLayout';
import { hasEnoughSpace } from '@player/utils/hasEnoughSpace';
export var LayoutOrientation;
(function (LayoutOrientation) {
    LayoutOrientation["PORTRAIT"] = "portrait";
    LayoutOrientation["LANDSCAPE"] = "landscape";
})(LayoutOrientation || (LayoutOrientation = {}));
export var VideoOrientation;
(function (VideoOrientation) {
    VideoOrientation["VERTICAL"] = "vertical";
    VideoOrientation["HORIZONTAL"] = "horizontal";
})(VideoOrientation || (VideoOrientation = {}));
export var LayoutSize;
(function (LayoutSize) {
    LayoutSize["SMALL"] = "small";
    LayoutSize["LARGE"] = "large";
})(LayoutSize || (LayoutSize = {}));
export const MIN_LEGIBLE_WING_SPACE = 520;
/**
 * Hook to help determine the variables which affect
 * the video player layout.
 *
 * Note: The returned values do not represent the actual layout of the environment.
 *       Rather they represent the layout that should be rendered.
 *       ex: layoutOrientation can be portrait when the device is landscape.
 *       This is intentional.
 *
 * @returns
 *  - videoOrientation The orientation of video element to be rendered
 *  - layoutOrientation The orientation of the layout to be rendered (device orientation)
 *  - layoutSize The size of the layout to be rendered
 */
export function usePlayerLayout() {
    const video = useStore((state) => state.video);
    const params = useStore((state) => state.params);
    const appContext = useStore((state) => state.appContext);
    const availableWidth = useVideoPlayerStore(videoPlayerAvailableWidthSelector);
    const availableHeight = useVideoPlayerStore(videoPlayerAvailableHeightSelector);
    const availableOrientation = useLayoutOrientation();
    const layout = useVideoPlayerStore(videoPlayerLayoutSelector);
    const isMinimized = useVideoPlayerStore(videoPlayerIsMinimizedSelector);
    const { isActiveWatch } = useLiveStreamActiveUser(video);
    const { products } = useProducts();
    return useMemo(() => {
        const videoWidth = Math.round(availableHeight * (9 / 16));
        const layoutOrientation = availableOrientation === LayoutOrientation.LANDSCAPE &&
            !forceSmallPortraitLayout()
            ? LayoutOrientation.LANDSCAPE
            : LayoutOrientation.PORTRAIT;
        const isCover = areStoryblockParams(params) && isTruthy(params?.cover);
        const isLayoutRelative = layout === PlayerLayout.DEFAULT;
        const isHorizontal = isVideoHorizontal(video);
        // How should the video be oriented in the layout
        const videoOrientation = isLayoutRelative && !isCover
            ? VideoOrientation.VERTICAL
            : isHorizontal
                ? VideoOrientation.HORIZONTAL
                : VideoOrientation.VERTICAL;
        const hasEnoughSpaceForWings = availableWidth - videoWidth > MIN_LEGIBLE_WING_SPACE ||
            videoOrientation === VideoOrientation.HORIZONTAL;
        // How should the layout be sized in regards of available space
        const layoutSize = hasEnoughSpace(availableWidth, availableHeight)
            ? LayoutSize.LARGE
            : LayoutSize.SMALL;
        // HeroUnits use storyblock and initially renders without storyblock wings.
        // In fullscreen mode, HeroUnits render WingWrappers which contain the ChatWing and ShoppingWing.
        // After closing HeroUnit video from fullscreen, WingWrappers remain rendered and disrupt the layout.
        // shouldRenderWingWrapper is used to prevent WingWrappers from rendering for HeroUnits not in fullscreen.
        const isHeroUnit = appContext?.appContextType === AppContextType.EMBED_HERO_CONTEXT_TYPE;
        const shouldRenderWingWrapper = !isHeroUnit || layout === PlayerLayout.FULLSCREEN;
        const shouldRenderShoppingWing = !!(hasEnoughSpaceForWings &&
            layout === PlayerLayout.FULLSCREEN &&
            layoutOrientation === LayoutOrientation.LANDSCAPE &&
            (videoOrientation === VideoOrientation.VERTICAL ||
                (videoOrientation === VideoOrientation.HORIZONTAL &&
                    layoutSize === LayoutSize.LARGE)) &&
            isLiveStreamLiveOrReplay(video) &&
            isActiveWatch &&
            params?.lsdesktop);
        const shouldRenderChatWing = isLiveStream(video) &&
            !!(shouldRenderShoppingWing &&
                videoOrientation === VideoOrientation.VERTICAL &&
                params?.lsdesktop);
        const shouldRenderShoppingModal = !shouldRenderShoppingWing &&
            (isLiveStreamLiveOrReplay(video) || (products && products.length > 0));
        const shouldRenderFooterChat = isLiveStream(video) && !shouldRenderChatWing && availableWidth > 250; // Not worthy to render footer chat when width is too small
        const shouldRenderNavLinks = isLiveStream(video)
            ? !isActiveWatch &&
                (layoutSize === LayoutSize.LARGE ||
                    layoutOrientation === LayoutOrientation.LANDSCAPE)
            : !(isMinimized && isHorizontal && !isLiveStream(video)); // CS-7248 - Hide nav links when minimized and horizontal
        return {
            layoutSize,
            layoutOrientation,
            videoOrientation,
            // Helpers
            shouldRenderShoppingWing,
            shouldRenderChatWing,
            shouldRenderShoppingModal,
            shouldRenderNavLinks,
            shouldRenderFooterChat,
            shouldRenderWingWrapper,
        };
    }, [
        availableHeight,
        availableOrientation,
        params,
        layout,
        video,
        availableWidth,
        isActiveWatch,
        products,
        isMinimized,
        appContext,
    ]);
}
